import {abilityFeature} from '../abilityFeature';
import {checkSiteAuthorizations} from '@packages/domains/authorization/abilities';
import {getPageNodes} from '@packages/shared/utils/PageTreeUtils';
import {resolvePageRestrictionOrigin} from '@packages/shared/utils/resolvePageRestrictionOrigin';
import type {DesignerStores} from '@packages/systems/designer/flux';
import type {
  ExpectedDiagnostics,
  ExtendedAbilitySelectorOptions,
  ExtendedDiagnosticAbilitySelectorOptions,
} from '../types';

type AuthorizationAbilityStores = Pick<
  DesignerStores,
  'AuthorizationsStore' | 'CurrentCollectionStore' | 'SiteDataStore'
>;

type AuthorizationSelectorOptions =
  | ExtendedAbilitySelectorOptions
  | ExtendedDiagnosticAbilitySelectorOptions
  | undefined;

type AuthorizationContextState = AuthorizationAbilityStores &
  Partial<
    Pick<
      DesignerStores,
      | 'DesignerStore'
      | 'LocalizationStore'
      | 'PageManagerUIStore'
      | 'PageStore'
      | 'SiteBranchStore'
    >
  >;

const resolvePageSettingsId = (state: AuthorizationContextState) => {
  const pageManagerState = state.PageManagerUIStore;
  if (!pageManagerState || typeof pageManagerState.get !== 'function') {
    return undefined;
  }

  const selectedItem = pageManagerState.get('selectedItem');
  if (!selectedItem || typeof selectedItem.get !== 'function') {
    return undefined;
  }

  return selectedItem.get('id') ?? undefined;
};

/**
 * Maps a working / branch page id to the SitePage id used for FGAC facts.
 * Source-backed site-branch pages use `branchMainPageId`; branch-local pages
 * keep their own id; legacy page-branches keep `parentId`. Folders never
 * become a SitePage origin.
 *
 * Uses `getPageNodes` (not `getPage`) so folder nodes are visible —
 * `getPage` filters via `isPage` and would miss folders, incorrectly falling
 * back to the folder id as a SitePage resource.
 */
const resolveSitePageId = (
  pageId: string | undefined,
  pageStore: DesignerStores['PageStore'] | undefined
): string | undefined => {
  if (!pageId || !pageStore) {
    return pageId;
  }

  const page = getPageNodes(pageStore).get(pageId);
  if (!page) {
    return pageId;
  }

  return resolvePageRestrictionOrigin({
    id: page.id ?? pageId,
    type: page.type,
    parentId: page.parentId,
    branchId: page.branchId,
    branchRole: page.branchRole,
    branchMainPageId: page.branchMainPageId,
  });
};

const resolveResourceContext = (
  state: AuthorizationAbilityStores,
  options: AuthorizationSelectorOptions
) => {
  const contextualState = state as AuthorizationContextState;
  const designerStore = contextualState.DesignerStore;

  const pageStore = contextualState.PageStore;
  const rawPageId =
    options?.pageId ?? designerStore?.currentPageId ?? undefined;
  const pageId = resolveSitePageId(rawPageId, pageStore);
  const branchId =
    options?.branchId ??
    (rawPageId && pageStore
      ? getPageNodes(pageStore).get(rawPageId)?.branchId
      : undefined) ??
    contextualState.SiteBranchStore?.activeSiteBranchId ??
    designerStore?.branchId ??
    undefined;
  const pageSettingsId =
    options?.pageSettingsId ?? resolvePageSettingsId(contextualState);
  const localeId =
    options?.localeId ?? contextualState.LocalizationStore?.currentLocale?._id;

  return {pageId, branchId, pageSettingsId, localeId};
};

export const authorizations = abilityFeature({
  key: 'authorizations',
  isValid: function isAuthorizationsValid(state, config, options) {
    const collectionId = options?.collectionId;
    const {pageId, branchId, pageSettingsId, localeId} = resolveResourceContext(
      state,
      options
    );

    return Boolean(
      state.AuthorizationsStore &&
      checkSiteAuthorizations(config)(state, {
        legacyAuthorizationOptions: {
          currentCollectionId: collectionId,
        },
        authorizationOptions: {
          SiteCollection: {
            currentResourceId: collectionId,
          },
          SiteLocale: {
            currentResourceId: localeId,
          },
          SitePage: {
            currentResourceId: pageId,
          },
          SiteBranch: {
            currentResourceId: branchId,
          },
          SitePageSettings: {
            currentResourceId: pageSettingsId,
          },
        },
      })
    );
  },
  diagnose: function diagnoseAuthorizations(state, config, options) {
    let isValid = true;
    const expectations: ExpectedDiagnostics<'authorizations'> = {};

    if (!state.AuthorizationsStore) {
      // When store is missing, checkSiteAuthorizations is permissive by default
      return [true, {}, undefined];
    }

    const collectionId = options?.collectionId;
    const {pageId, branchId, pageSettingsId, localeId} = resolveResourceContext(
      state,
      options
    );

    const authorizationOptions = {
      legacyAuthorizationOptions: {currentCollectionId: collectionId},
      authorizationOptions: {
        SiteCollection: {currentResourceId: collectionId},
        SiteLocale: {currentResourceId: localeId},
        SitePage: {currentResourceId: pageId},
        SiteBranch: {currentResourceId: branchId},
        SitePageSettings: {currentResourceId: pageSettingsId},
      },
    };

    for (const [authTest, expectedAccess] of Object.entries(config)) {
      const passes = checkSiteAuthorizations({[authTest]: expectedAccess})(
        state,
        authorizationOptions
      );
      if (passes) continue;

      isValid = false;
      expectations[authTest] = {
        expected: expectedAccess,
        received: !expectedAccess,
      };
    }

    return [isValid, expectations, undefined];
  },
});
